import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Building2, ChevronDown } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import type { Organization } from '@/types/organization';

interface OrganizationSwitcherProps {
  organizations: Organization[];
  selectedOrganization: Organization | null;
  onSwitchOrganization: (orgId: string) => void;
  disabled?: boolean;
}

export function OrganizationSwitcher({
  organizations,
  selectedOrganization,
  onSwitchOrganization,
  disabled = false
}: OrganizationSwitcherProps) {
  const { t, dir, language } = useLanguage();
  const [open, setOpen] = useState(false);

  if (!selectedOrganization || organizations.length === 0) {
    return null;
  }

  const getOrganizationDisplayName = (org: Organization) => {
    if (language === 'ar') {
      return org.name || org.nameEn || 'Unknown Organization';
    } else {
      return org.nameEn || org.name || 'Unknown Organization';
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-auto px-4 py-3 w-full hover:bg-transparent focus:bg-transparent active:bg-transparent"
          disabled={disabled}
        >
          <div className="flex items-center justify-center w-full">
            <div className={`flex items-center gap-3 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
              {organizations.length > 1 && (
                <ChevronDown className={`h-5 w-5 text-muted-foreground ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
              )}
              <div className={`text-center ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>
                <p className={`font-medium leading-none ${language === 'en' ? 'text-base' : 'text-lg'}`}>
                  {getOrganizationDisplayName(selectedOrganization)}
                </p>
                {language === 'en' && selectedOrganization.name && selectedOrganization.name !== selectedOrganization.nameEn && (
                  <p className="text-base text-muted-foreground">
                    {selectedOrganization.name}
                  </p>
                )}
                {language === 'ar' && organizations.length > 1 && selectedOrganization.nameEn && selectedOrganization.nameEn !== selectedOrganization.name && (
                  <p className="text-xs text-muted-foreground">
                    {selectedOrganization.nameEn}
                  </p>
                )}
              </div>
              {selectedOrganization.logoUrl ? (
                <div className="flex-shrink-0">
                  <img 
                    src={selectedOrganization.logoUrl} 
                    alt={getOrganizationDisplayName(selectedOrganization)}
                    className="h-16 w-auto max-w-[76px] object-contain"
                  />
                </div>
              ) : (
                <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
                  <Building2 className="h-8 w-8 text-muted-foreground" />
                </div>
              )}
            </div>
          </div>
        </Button>
      </DropdownMenuTrigger>
      
      {organizations.length > 1 && (
        <DropdownMenuContent 
          align={dir === 'rtl' ? 'start' : 'end'} 
          className={`w-56 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}
        >
          {organizations.map((org) => (
            <DropdownMenuItem
              key={org.id}
              onClick={() => {
                onSwitchOrganization(org.id);
                setOpen(false);
              }}
              className={`flex items-center gap-2 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}
              dir={dir}
            >
              {org.logoUrl ? (
                <Avatar className="h-6 w-6">
                  <AvatarImage src={org.logoUrl} alt={getOrganizationDisplayName(org)} />
                  <AvatarFallback className="text-xs">
                    <Building2 className="h-3 w-3" />
                  </AvatarFallback>
                </Avatar>
              ) : (
                <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center">
                  <Building2 className="h-3 w-3 text-muted-foreground" />
                </div>
              )}
              <span className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                {getOrganizationDisplayName(org)}
              </span>
              {selectedOrganization.id === org.id && (
                <div className={`ml-auto ${dir === 'rtl' ? 'mr-auto ml-0' : ''}`}>
                  <div className="h-2 w-2 rounded-full bg-primary" />
                </div>
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      )}
    </DropdownMenu>
  );
}
